import { ABILITIES, abilityMod } from './formulas';
import { listSkills, skillAbility } from './skillsLookup';

// Compute total + breakdown for each SRD skill.
//
// `proficientSkills` is a Set of skill ids (e.g. 'athletics', 'medicine').
// `expertiseSkills` is a Set for double-proficiency (Phase 3+ — Rogue's
// Expertise, Bard's Expertise). For now we accept it for forward-compat but
// no archetype uses it.
//
// Returns rows sorted by skill name (UI consumes the order directly):
//   [{
//     id, name, ability, proficient, expertise,
//     total, breakdown: [{source, value}],
//   }, ...]

export const computeSkills = ({
  abilities,
  pb,
  proficientSkills = new Set(),
  expertiseSkills = new Set(),
}) => {
  const skills = listSkills();
  return skills
    .map((skill) => {
      const ab = skill.ability || skillAbility(skill.id);
      const abMod = abilityMod(abilities[ab] || 0);
      const proficient = proficientSkills.has(skill.id);
      const expertise = expertiseSkills.has(skill.id);

      let total = abMod;
      const breakdown = [{ source: `${ab.toUpperCase()} modifier`, value: abMod }];
      if (proficient) {
        const profValue = expertise ? pb * 2 : pb;
        total += profValue;
        breakdown.push({
          source: expertise ? 'Proficiency × 2 (Expertise)' : 'Proficiency Bonus',
          value: profValue,
        });
      }

      return {
        id: skill.id,
        name: skill.name,
        ability: ab,
        proficient,
        expertise,
        total,
        breakdown,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};

// Convenience: only the rows where the character is proficient.
export const computeProficientSkills = (args) =>
  computeSkills(args).filter((row) => row.proficient);

// Passive perception = 10 + Wis mod + (PB if proficient in Perception)
//   + situational bonuses (Observant feat, etc. — Phase 3+).
export const computePassivePerception = ({ abilities, pb, proficientSkills = new Set() }) => {
  const wisMod = abilityMod(abilities.wis || 0);
  const proficient = proficientSkills.has('perception');
  const breakdown = [
    { source: 'Base', value: 10 },
    { source: 'WIS modifier', value: wisMod },
  ];
  let total = 10 + wisMod;
  if (proficient) {
    total += pb;
    breakdown.push({ source: 'Perception proficiency', value: pb });
  }
  return { total, breakdown, proficient };
};

// Helper used by buildPreview to validate the ABILITIES enumeration is the
// same one the rest of the engine assumes (no drift).
export const ALL_ABILITIES = ABILITIES;
